import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

const AUTH_STORAGE_KEY = 'auth-storage';

/** Returns true if localStorage has valid auth (token + isAuthenticated). */
function hasValidAuthInStorage(): boolean {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { state?: { token?: string; isAuthenticated?: boolean } };
    const state = parsed?.state;
    return !!(state?.token && state?.isAuthenticated);
  } catch {
    return false;
  }
}

interface PlayerProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route specifically for Player routes.
 * Redirects to /login if not authenticated.
 * Handles back-button after logout: when page is restored from bfcache, re-checks localStorage
 * and redirects to login if auth was cleared (no auto-login from cache).
 */
export const PlayerProtectedRoute = ({ children }: PlayerProtectedRouteProps) => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isHydrated, setIsHydrated] = useState(false);
  const [forceRedirect, setForceRedirect] = useState(false);

  // When page is restored from back/forward cache (user pressed Back after logout),
  // re-validate auth from localStorage. If no valid auth, redirect to login (replace) so
  // the user cannot see protected content from cached state.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && !hasValidAuthInStorage()) {
        setForceRedirect(true);
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [navigate]);

  // Wait for zustand persist to hydrate from localStorage
  useEffect(() => {
    const checkHydration = () => {
      try {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) {
          setTimeout(() => setIsHydrated(true), 50);
        } else {
          setIsHydrated(true);
        }
      } catch {
        setIsHydrated(true);
      }
    };
    checkHydration();
  }, []);

  // Don't do anything if we're on login/register pages
  const isPublicPage = location.pathname === '/login' || 
                       location.pathname === '/register' || 
                       location.pathname === '/';
  
  if (isPublicPage) {
    return <>{children}</>;
  }

  // After back-from-cache we redirect via navigate(); show loading until redirect completes
  if (forceRedirect) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Wait for hydration before making redirect decisions
  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Re-validate from localStorage on each render for protected routes (guards against stale zustand after logout in another tab or bfcache)
  if (!hasValidAuthInStorage()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // If not authenticated in store, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};
